/**
 * AddEditQuickTapV2 — full V2 port of the Quick Tap edit form, built
 * on the same `src/components/redesign/edit` primitives the Sessions
 * V2 form uses.
 *
 * Architecture mirrors v1 1:1 — same state shape, fetch graph,
 * media-upload helpers, and submit handler — so the save flow is
 * byte-identical and we don't risk regressing the API contract.
 *
 * Sections (each is a FormSection card):
 *   01 Basic info       — title, description, author
 *   02 Parent           — category or subcategory only
 *   03 Tags             — TagsInput (edit-only, diff-based per-add /
 *                         per-remove API calls — same as Sessions)
 *   04 Sharing & Media  — 2 upload tiles + universal link
 *   05 Video            — VideoTile (Mux playback or pending blob)
 *   06 Background music — BgmCard (no lock toggle — QT doesn't have
 *                         is_bgm_locked)
 *   07 Tapping point timings — TappingPointsEditor with the v2
 *                              timeline + row list + import modal
 *   08 Danger zone (edit-only) — delete button
 *
 * Right rail:
 *   • PreviewCardQT  — live `.lib-tile` preview
 *   • PublishingCardQT — meta dl + active/featured pills
 *   • SettingsCardQT — 5 publishing toggles
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';

import LoadingBackdrop from '../../Common/LoadingBackdrop';

import { fetchAllBGM } from '../../../actions/backgroundMusicActions';
import { fetchAuthors } from '../../../actions/authorsActions';
import { fetchAllCategories } from '../../../actions/categoryActions';
import { clearMedia, uploadImage, uploadQuickTapVideo } from '../../../actions/mediaActions';
import {
  addEditQuickTapSession,
  deleteQuickTapSession,
  fetchOneQuickTapSession,
} from '../../../actions/quickTapsActions';
import { fetchAllSubCategories } from '../../../actions/subcategoryActions';
import { fetchTappingPoints } from '../../../actions/tappingPointsActions';
import { addObjectTag, removeObjectTag } from '../../../actions/tagActions';
import { preventEnterFormSubmission } from '../../../utils/formUtils';

import { Button } from '../../redesign';
import {
  FormSection,
  FieldBlock,
  FieldRow,
  TextInput,
  Textarea,
  TagsInput,
  LinkField,
  AuthorSelectButton,
  ParentSelectButton,
  UploadTile,
  VideoTile,
  BgmCard,
} from '../../redesign/edit';
import TappingPointsEditor from '../../AddEditSession/v2/TappingPointsEditor';
import PublishingCardQT from './PublishingCardQT';
import SettingsCardQT from './SettingsCardQT';
import PreviewCardQT from './PreviewCardQT';

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function AddEditQuickTapV2({ isEdit, selectedId, onLoaded }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* ─── Selectors (1:1 with v1) ──────────────────────────────────── */
  const tapping_points_raw = useSelector((s) => s.tapping_points?.tapping_points);
  const tapping_points = useMemo(
    () =>
      Array.isArray(tapping_points_raw)
        ? tapping_points_raw
        : tapping_points_raw
          ? Object.values(tapping_points_raw)
          : [],
    [tapping_points_raw],
  );
  const background_musics = useSelector((s) => s.background_musics?.background_musics);
  const quick_tap_image = useSelector((s) => s.media?.image);
  const video = useSelector((s) => s.media?.video);

  /* ─── State (1:1 with v1) ──────────────────────────────────────── */
  const [formData, setFormData] = useState({
    quick_tap_title: '',
    quick_tap_description: '',
    quick_tap_is_new: 0,
    quick_tap_is_featured: 0,
    quick_tap_is_premium: 0,
    quick_tap_is_active: 1,
    quick_tap_is_dark_mode: 0,
    quick_tap_video_playback_id: '',
    quick_tap_deep_link_image: '',
    quick_tap_created_at: '',
    quick_tap_updated_at: '',
    quick_tap_bg_music_url: '',
    quick_tap_image: '',
    quick_tap_duration: '',
    quick_tap_duration_in_sec: 0,
    quick_tap_author_id: '',
    video_url_preview: '',
    video_uploading: false,
    image_preview: '',
    image_uploading: false,
    deep_link_image_preview: '',
    deep_link_image_uploading: false,
    session_bgm_id: '0',
    bg_timestamps: [{ tapping_point_id: '', timestamp: 0 }],
    tags: [],
    associatedParent: '0',
    category_id: 0,
    subcategory_id: 0,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const timestampComponentRef = useRef(null);

  // Latest-value ref for the page chrome's onLoaded callback —
  // lets the load effect stay keyed on `selectedId` alone.
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  /* ─── Load data (1:1 with v1) ──────────────────────────────────── */
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        dispatch(clearMedia());
        await Promise.all([
          dispatch(fetchAllCategories(1)),
          dispatch(fetchAllSubCategories(0, 1)),
          dispatch(fetchAuthors()),
          dispatch(fetchTappingPoints()),
          dispatch(fetchAllBGM()),
        ]);
        if (isEdit) {
          setIsLoadingSession(true);
          try {
            const sessionData = await dispatch(fetchOneQuickTapSession(selectedId));
            if (sessionData) {
              let associatedParent = '0';
              if (sessionData.category?.category_id) {
                associatedParent = sessionData.category.category_id + 'CAT';
              } else if (sessionData.subcategory?.subcategory_id) {
                associatedParent = sessionData.subcategory.subcategory_id + 'SUB';
              }

              setFormData((prev) => ({
                ...prev,
                quick_tap_id: sessionData.id,
                quick_tap_title: sessionData.title,
                quick_tap_description: sessionData.description || '',
                quick_tap_is_new: sessionData.is_new,
                quick_tap_is_featured: sessionData.is_featured,
                quick_tap_is_premium: sessionData.is_premium,
                quick_tap_is_active: sessionData.is_active,
                quick_tap_is_dark_mode: sessionData.is_dark_mode,
                quick_tap_video_playback_id: sessionData.video_playback_id || '',
                quick_tap_duration: sessionData.duration || '',
                quick_tap_duration_in_sec: sessionData.duration_in_sec || 0,
                quick_tap_deep_link_image: sessionData.deep_link_image || '',
                universal_link: sessionData.universal_link || '',
                quick_tap_created_at: sessionData.created_at || '',
                quick_tap_updated_at: sessionData.updated_at || '',
                quick_tap_bg_music_url: sessionData.bg_music_url || '',
                quick_tap_image: sessionData.image || '',
                quick_tap_author_id: sessionData.author_id || '',
                bg_timestamps:
                  sessionData.bg_timestamps?.length > 0
                    ? sessionData.bg_timestamps
                    : [{ tapping_point_id: '', timestamp: 0 }],
                tags: sessionData.tags || [],
                associatedParent,
              }));
              // Lift title + updated_at to the page chrome's
              // sticky header so it doesn't need its own parallel
              // GET /quick-tap-sessions/{id}.
              if (onLoadedRef.current) {
                onLoadedRef.current({
                  title: sessionData.title,
                  updated_at: sessionData.updated_at || null,
                });
              }
            } else {
              setError('Failed to load quick tap data.');
            }
          } catch {
            setError('Failed to load quick tap data.');
          } finally {
            setIsLoadingSession(false);
          }
        }
        setIsLoading(false);
      } catch {
        setError('Failed to load required data. Please try again.');
        setIsLoading(false);
      }
    };
    loadData();
  }, [dispatch, isEdit, selectedId]);

  /* ─── BGM matching: pick the bgm_id whose url matches the saved
   *  quick_tap_bg_music_url. Same effect as v1; runs once the BGM
   *  catalog is loaded. */
  useEffect(() => {
    if (isEdit && background_musics?.length > 0 && formData.quick_tap_bg_music_url) {
      const bgmId =
        background_musics.find((bgm) => bgm.bgm_url === formData.quick_tap_bg_music_url)?.bgm_id || '0';
      setFormData((prev) =>
        String(bgmId) !== prev.session_bgm_id
          ? { ...prev, session_bgm_id: String(bgmId) }
          : prev,
      );
    }
  }, [background_musics, isEdit, formData.quick_tap_bg_music_url]);

  /* ─── Media side effects (1:1 with v1) ────────────────────────── */
  useEffect(() => {
    if (quick_tap_image && formData.image_uploading) {
      setFormData((prev) => ({ ...prev, quick_tap_image, image_uploading: false }));
      dispatch(clearMedia());
    }
    if (quick_tap_image && formData.deep_link_image_uploading) {
      setFormData((prev) => ({
        ...prev,
        quick_tap_deep_link_image: quick_tap_image,
        deep_link_image_uploading: false,
      }));
      dispatch(clearMedia());
    }
    if (video && formData.video_uploading) {
      setFormData((prev) => ({
        ...prev,
        video_uploading: false,
        quick_tap_video_playback_id: video.playback_id,
        quick_tap_duration: video.duration,
        quick_tap_duration_in_sec: video.duration_in_sec,
      }));
      dispatch(clearMedia());
    }
  }, [
    quick_tap_image,
    video,
    dispatch,
    formData.image_uploading,
    formData.deep_link_image_uploading,
    formData.video_uploading,
  ]);

  /* ─── Handlers (1:1 with v1) ──────────────────────────────────── */
  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setField(name, value);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.image_preview) URL.revokeObjectURL(formData.image_preview);
    setFormData((prev) => ({
      ...prev,
      image_preview: URL.createObjectURL(file),
      image_uploading: true,
    }));
    await dispatch(uploadImage(file));
  };

  const handleDeepLinkImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.deep_link_image_preview) URL.revokeObjectURL(formData.deep_link_image_preview);
    setFormData((prev) => ({
      ...prev,
      deep_link_image_preview: URL.createObjectURL(file),
      deep_link_image_uploading: true,
    }));
    await dispatch(uploadImage(file));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (formData.video_url_preview) URL.revokeObjectURL(formData.video_url_preview);
    setFormData((prev) => ({
      ...prev,
      video_url_preview: URL.createObjectURL(file),
      video_uploading: true,
    }));
    await dispatch(uploadQuickTapVideo(file));
  };

  /* ─── Timestamps ───────────────────────────────────────────── */
  const handleTimestampChange = (value, stateProp, index, field) => {
    setFormData((prev) => ({
      ...prev,
      bg_timestamps: prev.bg_timestamps.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const addTimestamp = () => {
    const newIndex = formData.bg_timestamps.length;
    setFormData((prev) => ({
      ...prev,
      bg_timestamps: [...prev.bg_timestamps, { tapping_point_id: '', timestamp: 0 }],
    }));
    if (timestampComponentRef.current) {
      timestampComponentRef.current.focusOnNewItem(newIndex);
    }
  };

  const removeTimestamp = (index) => {
    setFormData((prev) => ({
      ...prev,
      bg_timestamps: prev.bg_timestamps.filter((_, i) => i !== index),
    }));
  };

  const handleImportTimestamps = (imported) => {
    setFormData((prev) => ({
      ...prev,
      bg_timestamps: [
        ...prev.bg_timestamps,
        ...imported.map((ts) => ({
          tapping_point_id: ts.tapping_point_id,
          timestamp: ts.timestamp,
          image_url: ts.image_url,
        })),
      ],
    }));
  };

  /* ─── Submit (mirrors v1) ──────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.quick_tap_author_id) {
      Swal.fire({ icon: 'error', text: 'You must select an Author for this Quick Tap.' });
      return;
    }
    let { bg_timestamps: timestamps_json, ...rest } = formData;
    timestamps_json = timestamps_json.filter((obj) => obj.tapping_point_id !== '');
    const times = timestamps_json.map((obj) => obj.timestamp);
    const uniqueTimes = [...new Set([...times])];
    if (times.length !== uniqueTimes.length) {
      Swal.fire({ icon: 'error', text: 'Cannot have two tapping points at same time.' });
      return;
    }
    let category_id = 0;
    let subcategory_id = 0;
    if (formData.associatedParent.indexOf('CAT') > -1) {
      category_id = parseInt(formData.associatedParent, 10);
    } else if (formData.associatedParent.indexOf('SUB') > -1) {
      subcategory_id = parseInt(formData.associatedParent, 10);
    }
    rest.quick_tap_updated_at = null;
    rest.quick_tap_created_at = null;
    const options = {
      quick_tap_session_json: JSON.stringify(rest),
      timestamps_json: JSON.stringify(timestamps_json),
      category_id,
      subcategory_id,
    };
    try {
      setIsSaving(true);
      await dispatch(addEditQuickTapSession(options, () => navigate('/quick-taps')));
    } finally {
      setIsSaving(false);
    }
  };

  /* ─── Delete (mirrors v1) ─────────────────────────────────── */
  const handleDelete = async () => {
    if (formData.quick_tap_is_active) {
      Swal.fire({
        icon: 'error',
        text: "Can't delete this Quick Tap, please update it to inactive status before deleting",
      });
      return;
    }
    const result = await Swal.fire({
      icon: 'warning',
      text: 'Are you sure you want to delete this Quick Tap?',
      showConfirmButton: true,
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonText: 'Yes, delete!',
    });
    if (result.value) {
      dispatch(deleteQuickTapSession(formData.quick_tap_id, () => navigate('/quick-taps')));
    }
  };

  /* ─── Render BGM options (mirrors v1, just renamed prop) ──── */
  const renderBGMOptions = useMemo(() => {
    if (!background_musics) return [];
    const options = background_musics.map((obj) => ({
      label: `${obj.bgm_title}${obj.bgm_duration ? ` · ${obj.bgm_duration}` : ''}`,
      value: String(obj.bgm_id),
      // The BgmCard handler reads `session_music_url` off the option —
      // map quick_tap_bg_music_url through that key so the same card
      // can drive both surfaces without bespoke wiring.
      session_music_url: obj.bgm_url,
      prop: 'session_bgm_id',
    }));
    const none = { label: 'None', value: '0', session_music_url: '', prop: 'session_bgm_id' };
    return [none, ...options];
  }, [background_musics]);

  /* ─── Loading / error ─────────────────────────────────────── */
  if (isLoading || isLoadingSession) {
    return <LoadingBackdrop open />;
  }
  if (error) {
    return (
      <FormSection title="Couldn't load quick tap">
        <p className="text-coral" style={{ fontSize: 14, marginBottom: 12 }}>{error}</p>
        <Link to="/quick-taps" className="no-underline">
          <Button variant="default">Return to Quick Taps</Button>
        </Link>
      </FormSection>
    );
  }

  return (
    <>
      <LoadingBackdrop open={isSaving} />
      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] items-start"
        style={{ gap: 24 }}
      >
        <form
          id="quick-tap-form"
          onSubmit={handleSubmit}
          onKeyDown={preventEnterFormSubmission}
          autoComplete="off"
          style={{ minWidth: 0 }}
        >
          {/* 01 — Basic info */}
          <FormSection title="Basic info" num="01">
            <FieldBlock label="Title" required htmlFor="f-quick-tap-title">
              <TextInput
                id="f-quick-tap-title"
                name="quick_tap_title"
                value={formData.quick_tap_title}
                onChange={handleFieldChange}
                required
              />
            </FieldBlock>
            <FieldBlock label="Description" htmlFor="f-quick-tap-description">
              <Textarea
                id="f-quick-tap-description"
                name="quick_tap_description"
                value={formData.quick_tap_description}
                onChange={handleFieldChange}
                rows={3}
                placeholder="Enter a description for this Quick Tap"
              />
            </FieldBlock>
            <FieldBlock label="Author" required className="max-w-[480px]">
              <AuthorSelectButton
                value={formData.quick_tap_author_id}
                onChange={(value) => setField('quick_tap_author_id', value)}
              />
            </FieldBlock>
          </FormSection>

          {/* 02 — Parent (category or subcategory; quick-tap-flagged
              entries only — showQuickTaps=true filters the catalog) */}
          <FormSection title="Parent" num="02">
            <FieldBlock
              label="Container"
              hint="Quick Taps live under a Quick Tap category or subcategory."
              noMargin
            >
              <ParentSelectButton
                value={formData.associatedParent}
                onChange={(value) => setField('associatedParent', value)}
                allowedTypes={['category', 'subcategory']}
                showQuickTaps
              />
            </FieldBlock>
          </FormSection>

          {/* 03 — Tags (edit-only) */}
          {isEdit && (
            <FormSection title="Tags" num="03">
              <FieldBlock
                label="Quick Tap tags"
                hint="Powers search, recommendations, and category cross-linking"
              >
                <TagsInput
                  value={(formData.tags || []).map((t) => t.tag)}
                  onChange={(nextStrings) => {
                    const prev = formData.tags || [];
                    const prevStrings = prev.map((t) => t.tag);
                    const added = nextStrings.filter((t) => !prevStrings.includes(t));
                    const removed = prevStrings.filter((t) => !nextStrings.includes(t));
                    added.forEach((tag) => {
                      dispatch(
                        addObjectTag({
                          object_id: formData.quick_tap_id,
                          object_type: 'QuickTap',
                          tag,
                        }),
                      );
                    });
                    removed.forEach((tag) => {
                      dispatch(
                        removeObjectTag({
                          object_id: formData.quick_tap_id,
                          object_type: 'QuickTap',
                          tag,
                        }),
                      );
                    });
                    setField('tags', nextStrings.map((tag) => ({ tag, newTag: false })));
                  }}
                  placeholder="Type a tag and press enter…"
                />
              </FieldBlock>
            </FormSection>
          )}

          {/* 04 — Sharing & Media */}
          <FormSection title="Sharing & Media" num={isEdit ? '04' : '03'}>
            <FieldRow cols={2} minColWidth={240}>
              <UploadTile
                label="Quick Tap Image"
                required
                hint="In-app cover"
                value={formData.image_preview || formData.quick_tap_image}
                uploading={!!formData.image_uploading}
                accept="image/*"
                onChange={handleImageUpload}
                onRemove={() => {
                  if (formData.image_preview) URL.revokeObjectURL(formData.image_preview);
                  setFormData((prev) => ({
                    ...prev,
                    quick_tap_image: '',
                    image_preview: '',
                  }));
                }}
              />
              <UploadTile
                label="Deep Link Image"
                hint="Social share card"
                value={formData.deep_link_image_preview || formData.quick_tap_deep_link_image}
                uploading={!!formData.deep_link_image_uploading}
                accept="image/*"
                onChange={handleDeepLinkImageUpload}
                onRemove={() => {
                  if (formData.deep_link_image_preview) URL.revokeObjectURL(formData.deep_link_image_preview);
                  setFormData((prev) => ({
                    ...prev,
                    quick_tap_deep_link_image: '',
                    deep_link_image_preview: '',
                  }));
                }}
              />
            </FieldRow>
            {isEdit && formData.universal_link && (
              <div style={{ marginTop: 16 }}>
                <FieldBlock
                  label="Universal Link"
                  hint="Generated automatically · used for share & deep links"
                  noMargin
                >
                  <LinkField value={formData.universal_link} />
                </FieldBlock>
              </div>
            )}
          </FormSection>

          {/* 05 — Quick Tap video */}
          <FormSection
            title="Quick Tap video"
            num={isEdit ? '05' : '04'}
            right={
              formData.quick_tap_duration ? (
                <span className="font-mono text-text-muted" style={{ fontSize: 11.5 }}>
                  {formData.quick_tap_duration}
                </span>
              ) : null
            }
          >
            <VideoTile
              required
              hint="MP4 / MOV / WebM · transcoded to HLS by Mux"
              value={formData.video_url_preview || formData.quick_tap_video_playback_id}
              uploading={!!formData.video_uploading}
              onChange={handleVideoUpload}
              onRemove={() => {
                if (formData.video_url_preview) URL.revokeObjectURL(formData.video_url_preview);
                setFormData((prev) => ({
                  ...prev,
                  quick_tap_video_playback_id: '',
                  video_url_preview: '',
                  quick_tap_duration: '',
                  quick_tap_duration_in_sec: 0,
                }));
              }}
            />
          </FormSection>

          {/* 06 — Background music. No lock toggle (QT doesn't model
              is_bgm_locked). */}
          <FormSection title="Background music" num={isEdit ? '06' : '05'}>
            <BgmCard
              options={renderBGMOptions}
              selectedValue={formData.session_bgm_id}
              audioUrl={formData.quick_tap_bg_music_url}
              isLocked={false}
              isLoading={isLoading}
              showLockToggle={false}
              onSelectionChange={(newValue) => {
                setFormData((prev) => ({
                  ...prev,
                  session_bgm_id: newValue ? newValue.value : '0',
                  quick_tap_bg_music_url: newValue ? newValue.session_music_url : '',
                }));
              }}
              onRemove={() => {
                setFormData((prev) => ({
                  ...prev,
                  session_bgm_id: '0',
                  quick_tap_bg_music_url: '',
                }));
              }}
            />
          </FormSection>

          {/* 07 — Tapping point timings (reuses Sessions V2 editor) */}
          <FormSection
            title="Tapping point timings"
            num={isEdit ? '07' : '06'}
            right={
              <span className="text-text-muted" style={{ fontSize: 11.5 }}>
                {formData.bg_timestamps.length} marker{formData.bg_timestamps.length === 1 ? '' : 's'}
              </span>
            }
          >
            <TappingPointsEditor
              ref={timestampComponentRef}
              items={formData.bg_timestamps}
              catalog={tapping_points}
              removeTimestamp={removeTimestamp}
              stateProp="bg_timestamps"
              handleTimestampChange={handleTimestampChange}
              onImportTimestamps={handleImportTimestamps}
              onAddTimestamp={addTimestamp}
              totalDurationSec={formData.quick_tap_duration_in_sec}
            />
          </FormSection>

          {/* 08 — Danger zone */}
          {isEdit && (
            <FormSection title="Danger zone" num="08">
              <div className="flex items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                <Button variant="danger" onClick={handleDelete}>
                  Delete quick tap
                </Button>
                <span className="text-text-muted" style={{ fontSize: 12 }}>
                  Set the Quick Tap to inactive first. Permanent — cannot be undone.
                </span>
              </div>
            </FormSection>
          )}
        </form>

        {/* Right rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
          }}
        >
          <PreviewCardQT formData={formData} />
          {isEdit && (
            <PublishingCardQT
              formData={formData}
              onToggleActive={(next) => setField('quick_tap_is_active', next ? 1 : 0)}
            />
          )}
          <SettingsCardQT
            formData={formData}
            onChange={(name, value) => setField(name, value)}
          />
        </aside>
      </div>
    </>
  );
}
